import { Smartphone, X } from "lucide-react";
import { useSyncExternalStore } from "react";
import { Button } from "@/components/ui/button";
import { useMediaQuery, usePhoneFlag } from "@/hooks/use-capture";
import { isIos } from "@/lib/capture";
import { logger } from "@/lib/logger";

const DISMISSED_KEY = "mesh-capture-install-hint-dismissed";

/** Chromium's install prompt event; not in TypeScript's DOM types. */
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function isInstallPrompt(event: Event): event is InstallPromptEvent {
  return "prompt" in event && typeof event.prompt === "function";
}

// Chromium fires this once, early, often before the home screen renders, so it is caught as
// soon as this module loads (the capture layout imports it statically).
let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => {
  for (const listener of listeners) listener();
};

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    if (!isInstallPrompt(event)) return;
    event.preventDefault();
    deferred = event;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    notify();
  });
}

function watchPrompt(onChange: () => void) {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

const getPrompt = () => deferred;

/** Once, until dismissed, and never in the installed app. */
export function InstallHint() {
  const [dismissed, setDismissed] = usePhoneFlag(DISMISSED_KEY);
  const standalone = useMediaQuery("(display-mode: standalone)");
  const prompt = useSyncExternalStore(watchPrompt, getPrompt, () => null);
  const ios = typeof navigator !== "undefined" && isIos(navigator);
  if (dismissed || standalone) return null;

  async function install() {
    if (!prompt) return;
    try {
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === "accepted") setDismissed(true);
    } catch (error) {
      logger.warn("install_prompt_failed", { error: String(error) });
    }
    deferred = null;
    notify();
  }

  return (
    <div
      data-testid="install-hint"
      className="flex items-start gap-3 rounded-lg border bg-card p-3"
    >
      <Smartphone className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1 space-y-2">
        <p className="text-sm">
          Add to Home Screen so photos are kept safe and the app opens full screen.
        </p>
        {ios ? (
          <p className="text-sm text-muted-foreground">Tap Share, then Add to Home Screen.</p>
        ) : prompt ? (
          <Button size="sm" onClick={() => void install()}>
            Add to Home Screen
          </Button>
        ) : null}
      </div>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Dismiss"
        className="-mt-1 -mr-1 shrink-0"
        onClick={() => setDismissed(true)}
      >
        <X />
      </Button>
    </div>
  );
}
